import { useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import { NOBODY, isComplete } from '../signatories';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import SignoffSheet from './SignoffSheet';
import { policyForType, usesStages, sheetFor } from '../signoff-policy';
import type { TheWorld } from '../signoff-policy';
import { asDate } from '../utils';
import type { Build } from '../types';

/**
 * WHO HAS SIGNED THIS RELEASE OFF, AND WHO HAS NOT.
 *
 * THE SHAPE IS A REAL SHEET'S. CPFC's is a table — Title, Name, Signature, Date
 * — signed by four people, and on the copy that arrived only the developer's
 * row carried a date. So the honest state of a sign-off is PART DONE, for as
 * long as it takes, and the screen's job is to say which rows are outstanding
 * rather than to round the answer to yes or no.
 *
 * NOTHING HERE SIGNS FOR THE SIGNATORY. A signature is their own act, given
 * through a link sent to their own address — #216. The one exception is an
 * admin recording a row somebody could not sign, and it is never dressed up as
 * anything else: the row says so, names who did it, and carries the reason.
 */
export default function SignOff({ build }: { build: Build }) {
  const rows = useApp((s) => s.signoffs).filter((r) => r.build === build.build);
  /* `?? NOBODY`, never `?? []` — see the note on NOBODY. A fresh array here is
     a new reference on every read, and this screen hung on exactly that. */
  const named = useApp((s) => s.meta.signatories ?? NOBODY);
  const signoffPolicy = useApp((s) => s.meta.signoffPolicy);
  const panel = named.filter(isComplete);
  const role = useApp((s) => s.shownRole);
  const requestSignoff = useApp((s) => s.requestSignoff);
  const markGoneLive = useApp((s) => s.markGoneLive);
  const cutTheRelease = useApp((s) => s.cutTheRelease);
  const setMeta = useApp((s) => s.setMeta);
  const setView = useApp((s) => s.setView);
  const mayAsk = can(role, 'editSettings');
  const mayActFor = can(role, 'answerForSomebody');
  const sample = useSampleBoard();
  const inert = useSampleInert();

  /*
   * A STAGE BOARD RAISES A RELEASE FROM ITS CONFIGURED STAGES, NOT A TYPED NAME.
   *
   * When the board (or the release type's override) is on the stage model, the
   * signers live in the stages — `meta.signatories` is empty, so the legacy
   * "name one here" on-ramp below read as "Nobody is named" and forced a
   * throwaway signatory that the stage model then ignored. Resolve the release's
   * own policy through the same engine `requestSignoff` uses, so the screen can
   * offer the ask directly from the stages, with the real count of who they
   * resolve to. A release on a stages board is not re-tester-dropped — that is a
   * configuration choice on the type, exactly as `requestSignoff` treats it — so
   * the preview is `sheetFor` of the release policy, nothing forced.
   */
  const ownerName = useApp((s) => s.meta.projectOwnerName);
  const ownerPosition = useApp((s) => s.meta.projectOwnerPosition);
  const ownerEmail = useApp((s) => s.meta.projectOwnerEmail);
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const projectRoles = useApp((s) => s.projectRoles);
  const profiles = useApp((s) => s.profiles);
  const projectId = useApp((s) => s.projectId);
  const releasePolicy = policyForType(signoffPolicy, 'release');
  const onStages = usesStages(releasePolicy);
  const world: TheWorld = {
    owner: {
      name: (ownerName ?? '').trim(),
      position: (ownerPosition ?? '').trim(),
      email: (ownerEmail ?? '').trim(),
    },
    access,
    roles,
    projectRoles,
    profiles,
    projectId,
  };
  const stageSigners = onStages ? sheetFor(releasePolicy, world).length : 0;

  const [problem, setProblem] = useState('');
  const [asking, setAsking] = useState(false);
  const [marking, setMarking] = useState(false);
  const [cutting, setCutting] = useState(false);
  const [draft, setDraft] = useState({ name: '', position: '' });

  /*
   * NOT ASKED FOR YET. Shown only to somebody who could ask — a viewer reading
   * an old release does not need a button they cannot press, and "no sign-off"
   * on a release from before any of this existed is not a fault to report.
   */
  if (!build.signoffRequestedAt) {
    if (!mayAsk || build.pulled) return null;

    /*
     * A STAGE BOARD'S ON-RAMP — ask straight from the configured stages.
     *
     * The signers are the stages, so there is nothing to type here: the ask
     * lights up when the release's stages resolve to anybody, and when they do
     * not the route is to the stage editor in Settings, never a typed name the
     * stage model would discard. The legacy named/slots on-ramp is unchanged,
     * below.
     */
    if (onStages) {
      return (
        <div className="so-block">
          <div className="so-none">
            <span>
              <strong>No sign-off has been asked for.</strong>{' '}
              {stageSigners > 0
                ? `${stageSigners} ${stageSigners === 1 ? 'person signs' : 'people sign'} work off here.`
                : 'No stages name anybody to sign work off here yet.'}
            </span>
            {stageSigners > 0 ? (
              <button
                disabled={asking}
                {...inert('btn btn-small', async () => {
                  setAsking(true);
                  setProblem(await requestSignoff(build.id));
                  setAsking(false);
                })}
              >
                {asking ? 'Asking…' : 'Ask for sign-off'}
              </button>
            ) : (
              <button type="button" className="link" onClick={() => setView('settings')}>
                Set up the stages in Settings
              </button>
            )}
          </div>
          {problem && (
            <p className="so-problem" role="alert">
              {problem}
            </p>
          )}
        </div>
      );
    }

    const canAdd = draft.name.trim() !== '' && draft.position.trim() !== '';
    const addSignatory = () => {
      if (!canAdd) return;
      setMeta({
        signatories: [...named, { name: draft.name.trim(), position: draft.position.trim() }],
      });
      setDraft({ name: '', position: '' });
    };
    return (
      <div className="so-block">
        <div className="so-none">
          <span>
            <strong>No sign-off has been asked for.</strong>{' '}
            {panel.length > 0
              ? `${panel.length} ${panel.length === 1 ? 'person signs' : 'people sign'} work off here.`
              : 'Nobody is named as signing work off here yet.'}
          </span>
          {panel.length > 0 && (
            <button
              disabled={asking}
              {...inert('btn btn-small', async () => {
                setAsking(true);
                setProblem(await requestSignoff(build.id));
                setAsking(false);
              })}
            >
              {asking ? 'Asking…' : 'Ask for sign-off'}
            </button>
          )}
        </div>

        {/*
          * NAME ONE HERE, RATHER THAN BE SENT AWAY FOR IT — #421.
          * The release journey stopped dead when nobody was on the panel: the
          * button it needs was disabled and the only way to name a signatory
          * lived on another screen, so the person was pushed out of the release
          * mid-task with no route back. A name and a position is a whole
          * signatory (the same rule `isComplete` uses), so one can be added
          * right here, the ask lights up, and the flow continues. Email and
          * signing order still belong to the full panel — a press away.
          */}
        {panel.length === 0 && (
          <div className="so-add">
            <input
              className="input so-add-in"
              aria-label="Signatory name"
              placeholder="Name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              readOnly={sample.readOnly}
            />
            <input
              className="input so-add-in"
              aria-label="Position"
              placeholder="Position"
              value={draft.position}
              onChange={(e) => setDraft({ ...draft, position: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addSignatory();
              }}
              readOnly={sample.readOnly}
            />
            <button disabled={!canAdd} {...inert('btn btn-small', addSignatory)}>
              Add
            </button>
            <button type="button" className="link so-add-full" onClick={() => setView('settings')}>
              Set up the full panel in Settings
            </button>
          </div>
        )}
        {problem && (
          <p className="so-problem" role="alert">
            {problem}
          </p>
        )}
      </div>
    );
  }

  const signed = rows.filter((r) => r.signedAt);
  /*
   * EVERYBODY ANSWERING IS NOT EVERYBODY AGREEING — the same fault the document
   * screen had, on the other side of the same table. Without the second half
   * this said "Signed off. All 4 signed." over a sheet with a refusal on it,
   * and offered the button to cut a release the database was going to refuse.
   */
  const refused = rows.filter((r) => r.answer === 'rejected');
  const all = signed.length === rows.length && rows.length > 0 && refused.length === 0;

  /*
   * A WITHDRAWN OR ROLLED-BACK RELEASE IS NOT AWAITING SIGN-OFF. Once it is
   * pulled it is out, and nothing here can be cut or shipped — so asking for a
   * signature, or reading "cannot be cut until the rest have", is the "it still
   * asks for sign-off" fault Vinnie found. The card's pulled note above already
   * says what happened and why; this only records that sign-off no longer
   * applies (with how far it had got), and keeps the go-live date for a rollback
   * that had gone out. The unasked-for case is handled above — pulled returns
   * null there too.
   */
  if (build.pulled) {
    return (
      <div className="so-block">
        <div className="so-none">
          <span>
            <strong>Sign-off no longer applies.</strong> This release was{' '}
            {build.pulled === 'rolledback' ? 'rolled back' : 'withdrawn'}
            {signed.length ? `, after ${signed.length} of ${rows.length} signed` : ''}.
          </span>
        </div>
        {build.wentLiveAt && (
          <p className="so-live-said">
            Went live {asDate(build.wentLiveAt)}.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="so-block">
      <div className={`so-state ${refused.length ? 'is-refused' : all ? 'is-done' : 'is-waiting'}`}>
        {refused.length ? (
          <span>
            <strong>
              {refused.length === 1
                ? `${refused[0].name} rejected this release.`
                : `${refused.length} people rejected this release.`}
            </strong>{' '}
            Read what they said. It cannot be cut until it is answered.
          </span>
        ) : all ? (
          <span>
            <strong>Signed off.</strong> All {rows.length} signed.
          </span>
        ) : (
          <span>
            <strong>Not yet signed off.</strong> {signed.length} of {rows.length} signed. This
            cannot be cut until the rest have.
          </span>
        )}
      </div>

      {/* A stage sheet shows its stage headings, in the frozen order; a legacy
          release draws flat exactly as it always has — see SignoffSheet. */}
      <SignoffSheet
        rows={rows}
        policy={signoffPolicy}
        mayActFor={mayActFor}
        maySendLink={can(role, 'sendSigningLink')}
        locked={!!build.pulled}
      />

      {/* OFFERED ONLY WHEN IT CAN BE PRESSED. A button that is there to be
          refused teaches people to expect refusals and to stop reading them.
          The sentence above already says what is outstanding and why. */}

      {/*
        * CUT IT — the act approval earns, and the change Vinnie asked for on
        * 21 August: a release is prepared, signed off, and only then cut. It
        * used to be cut first and asked to approve afterwards, which put the
        * signature where it could not refuse anything.
        */}
      {mayAsk && all && !build.cutAt && !build.pulled && (
        <div className="so-live">
          <button
            disabled={cutting}
            {...inert('btn btn-small', async () => {
              setCutting(true);
              setProblem(await cutTheRelease(build.id));
              setCutting(false);
            })}
          >
            {cutting ? 'Cutting…' : 'Cut the release'}
          </button>
          <span className="so-hint">Everybody has signed, so this can be issued.</span>
        </div>
      )}

      {/*
        * AND GOING LIVE IS A SEPARATE DAY. A release approved on Tuesday and
        * deployed on Thursday night is the ordinary case, and a record that
        * cannot tell those apart cannot answer either question afterwards.
        */}
      {mayAsk && build.cutAt && !build.wentLiveAt && !build.pulled && (
        <div className="so-live">
          <button
            disabled={marking}
            {...inert('btn btn-small', async () => {
              setMarking(true);
              setProblem(await markGoneLive(build.id));
              setMarking(false);
            })}
          >
            {marking ? 'Recording…' : 'Mark as gone to live'}
          </button>
          <span className="so-hint">Cut {asDate(build.cutAt)}.</span>
        </div>
      )}
      {build.wentLiveAt && (
        <p className="so-live-said">
          Went live {asDate(build.wentLiveAt)}.
        </p>
      )}
      {problem && (
        <p className="so-problem" role="alert">
          {problem}
        </p>
      )}

      <p className="so-caveat">
        A signature is the signatory&rsquo;s own act. Where an admin records one for somebody who
        could not, the row says so and keeps the reason.
      </p>
    </div>
  );
}
